import { useState } from "react";

import {
  Grid,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  useTheme,
  Dialog,
  Box,
  Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { TextField } from "@aclymatepackages/atoms";

import ScopeThreeCategorySelect from "./ScopeThreeCategorySelect";
import OpenAiVendorCategorization from "./OpenAiVendorCategorization";

import { newVendorRelationship } from "../../../helpers/utils/apiCalls";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import {
  useAccountData,
  useMutateCachedCollection
} from "../../../helpers/firebase";

const NewVendorForm = ({
  setVendor,
  setShowNewVendorForm,
  transactionSubcategory
}) => {
  const { vendors } = useEmissionsContext();
  const [accountData] = useAccountData();
  const mutateCachedCollection = useMutateCachedCollection();
  const { palette } = useTheme();

  const [newVendor, setNewVendor] = useState({ id: "new-vendor" });
  const [isSaving, setIsSaving] = useState(false);
  const [pendingMerge, setPendingMerge] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const editNewVendor = (field) => (value) =>
    editObjectData(setNewVendor, field, value);

  const {
    id: existingVendorId,
    name = "",
    naicsCode,
    scopeThreeCategory
  } = newVendor;

  const isDuplicateVendorName = !!vendors.find(
    (vendor) => vendor.name.toLowerCase() === name.toLowerCase()
  );

  const isButtonDisabled =
    isDuplicateVendorName ||
    !name ||
    (transactionSubcategory === "spend-based" && !naicsCode);

  // Split out from onNewVendorSave so the same request can be resubmitted
  // with a mergeDecision once the user answers the "did you mean X?" prompt
  // below -- the server always re-derives the match itself rather than
  // trusting a client-supplied vendor id, so resubmitting is safe (mirrors
  // NewCustomerForm.js's onCompanySearchMergeDecision/onMergeDecision).
  const submitNewVendor = async (mergeDecision) => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const { id: buyerId, name: buyerName } = accountData;
      const response = await newVendorRelationship({
        ...newVendor,
        emissionCategory: transactionSubcategory,
        ...(existingVendorId !== "new-vendor" && {
          vendorId: existingVendorId
        }),
        vendorName: name,
        buyerId,
        buyerName,
        source: "manual-entry",
        ...(mergeDecision ? { mergeDecision } : {}),
        ...(mergeDecision === "merge" && pendingMerge
          ? { confirmedMatchedVendorId: pendingMerge.matchedVendorId }
          : {})
      });

      if (response?.requiresConfirmation) {
        setPendingMerge({
          matchedVendorId: response.matchedVendorId,
          matchedVendorName: response.matchedVendorName
        });
        return;
      }

      const { vendorName: resolvedVendorName, vendorId } = response || {};

      // Nothing to hand back to the parent form means the save didn't
      // actually happen -- the controller returns a bare null on any schema
      // failure and an { error: true } envelope on an auth failure, neither
      // of which carries a vendorId. Bailing out BEFORE closing the form (and
      // before invalidating the cache) keeps the user's input on screen with
      // a visible error, instead of the form vanishing and the vendor field
      // silently staying empty.
      if (!vendorId) {
        setSaveError(
          "We couldn't add that vendor. Please try again, or pick an existing vendor instead."
        );
        return;
      }

      setPendingMerge(null);
      mutateCachedCollection("vendor-relationships");
      setShowNewVendorForm(false);

      setVendor({
        ...newVendor,
        name: resolvedVendorName,
        vendorId,
        emissionCategory: transactionSubcategory,
        archived: false,
        id: vendorId
      });
    } catch (error) {
      // Surfaced, not swallowed: without this the request rejecting left the
      // confirmation dialog sitting open with no feedback and no way out.
      console.error("newVendorRelationship failed", error);
      setSaveError(
        "We couldn't add that vendor. Please try again, or pick an existing vendor instead."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onNewVendorSave = () => submitNewVendor();
  const onMergeDecision = (mergeDecision) => submitNewVendor(mergeDecision);

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <TextField
          label="Vendor Name"
          value={name}
          setValue={editNewVendor("name")}
          helperText="Please use the full vendor name (e.g. United Airlines instead of United)"
        />
      </Grid>
      <>
        {transactionSubcategory === "spend-based" && (
          <>
            <Grid item>
              <OpenAiVendorCategorization
                editVendor={editNewVendor}
                naicsCode={naicsCode}
              />
            </Grid>
            <Grid item>
              <ScopeThreeCategorySelect
                scopeThreeCategory={scopeThreeCategory}
                setScopeThreeCategory={editNewVendor("scopeThreeCategory")}
              />
            </Grid>
          </>
        )}
      </>
      {saveError && !pendingMerge && (
        <Grid item>
          <Typography variant="body2" color="error">
            {saveError}
          </Typography>
        </Grid>
      )}
      <Grid item container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Tooltip title="Back to select a vendor">
            <IconButton
              disabled={isSaving}
              onClick={() => setShowNewVendorForm(false)}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Button
            disabled={isButtonDisabled || isSaving}
            variant="contained"
            color="primary"
            sx={{ color: palette.common.white }}
            startIcon={
              isSaving ? (
                <CircularProgress size={16} sx={{ color: palette.common.white }} />
              ) : null
            }
            onClick={onNewVendorSave}
          >
            {isSaving ? "Adding Vendor..." : "Add Vendor"}
          </Button>
        </Grid>
      </Grid>
      {/* onClose (plus the explicit Go back button) matters here because a
          failed confirmation leaves this dialog open -- without a way out,
          the form's own back arrow is behind the modal and unreachable, so a
          persistently failing request would trap the user with no escape but
          a page reload. */}
      <Dialog
        open={!!pendingMerge}
        maxWidth="sm"
        fullWidth
        onClose={() => !isSaving && setPendingMerge(null)}
      >
        {pendingMerge && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            gap={2}
          >
            <Typography variant="h6" color="textPrimary" align="center">
              We found an existing vendor that looks like a match
            </Typography>
            <Typography variant="body1" color="textPrimary" align="center">
              Is &quot;{pendingMerge.matchedVendorName}&quot; the same vendor as &quot;{name}
              &quot;?
            </Typography>
            {saveError && (
              <Typography variant="body2" color="error" align="center">
                {saveError}
              </Typography>
            )}
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={isSaving}
                sx={{ color: palette.common.white }}
                onClick={() => onMergeDecision("merge")}
              >
                Yes, this is the same vendor
              </Button>
              <Button
                variant="outlined"
                disabled={isSaving}
                onClick={() => onMergeDecision("new")}
              >
                No, create a new vendor
              </Button>
            </Box>
            <Button
              size="small"
              disabled={isSaving}
              onClick={() => setPendingMerge(null)}
            >
              Go back without adding
            </Button>
          </Box>
        )}
      </Dialog>
    </Grid>
  );
};
export default NewVendorForm;
